import { useState } from "react";
import type { ReceiveSupplierOrder, SupplierOrder } from "@dentacore/shared-types";

import { formatMoney, minorUnitsPerUnit } from "../patients/money";
import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import { shortDate, typedMinorUnits } from "./supplier-text";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";

const PACKS = { min: 1, integer: true, unit: "packs" } as const;

/**
 * What a delivery of `packs` adds to the shelf — **the server's arithmetic, said
 * the same way** (IA-D13): a bulk item is counted in packs (FX22), so a box of a
 * hundred gloves arriving is one more box, not a hundred more of anything.
 */
export function arrivalWords(
  order: SupplierOrder,
  packs: number,
): { added: number; words: string } {
  if (order.itemKind === "BULK") {
    return { added: packs, words: `${String(packs)} pack${packs === 1 ? "" : "s"}` };
  }
  const added = packs * order.usesPerPack;
  return { added, words: `${String(added)} use${added === 1 ? "" : "s"}` };
}

/**
 * *It arrived* — STORY-171.
 *
 * The packs that came are added to the shelf as a RESTOCK movement. **The
 * invoice price is asked for**, and when it differs from what was ordered a
 * tick box — ticked to start with — saves it as the supplier's new price
 * (SU-D6), **shown only to someone who manages prices** (IA-D4): the supplier's
 * price is every location's, and the server ignores the box from anyone else.
 *
 * **A short delivery asks whether the rest is still coming** (IA-D3). *Still
 * coming* keeps the order on its way for the remainder; *Nothing more* closes it.
 */
export function ReceiveDialog({
  order,
  quantityOnHand,
  currency,
  busy,
  canManagePrices = false,
  onReceive,
  onClose,
}: {
  order: SupplierOrder;
  /** The item's count now, for the "→" line. Null when not known here. */
  quantityOnHand: number | null;
  currency: string;
  busy: boolean;
  /** Holds `inventory.items.manage`, so may save the invoice price as the supplier's. */
  canManagePrices?: boolean;
  onReceive: (body: ReceiveSupplierOrder) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const perUnit = minorUnitsPerUnit(currency);
  const decimals = Math.round(Math.log10(perUnit));
  // A part-delivered order opens on what is still to come.
  const outstanding = Math.max(1, order.packs - (order.receivedPacks ?? 0));
  const [packs, setPacks] = useState(String(outstanding));
  const [price, setPrice] = useState((order.priceCents / perUnit).toFixed(decimals));
  const [update, setUpdate] = useState(true);
  const [stillComing, setStillComing] = useState(true);

  const priceRules = { min: 0, integer: decimals === 0, unit: currency } as const;
  const packsValid = packs.trim() !== "" && describeNumberError(packs, PACKS) === null;
  // Built from the typed digits, never a float times a hundred.
  const cents = typedMinorUnits(price, currency);
  const changed = cents !== null && cents !== order.priceCents;
  const arrival = arrivalWords(order, packsValid ? Number(packs) : 0);
  const short = packsValid && Number(packs) < outstanding;

  return (
    <Modal label={`${order.itemName} arrived`} onClose={onClose} testId="receive-dialog">
      <form
        className={inv.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (!packsValid || cents === null) return;
          void onReceive({
            packs: Number(packs),
            priceCents: cents,
            updatePrice: canManagePrices && changed && update,
            stillComing: short && stillComing,
          }).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={inv.dialogTitle}>{order.itemName} arrived</h2>
        <p className={styles.sub}>
          Ordered from {order.supplierName} on {shortDate(order.orderedAt)}: {order.packs} ×{" "}
          {order.pack}
          {(order.receivedPacks ?? 0) > 0 && ` — ${String(order.receivedPacks)} already arrived`}
        </p>

        <div className={inv.dialogGrid}>
          <NumberField
            label="Packs received"
            required
            className={inv.field}
            labelClassName={inv.fieldLabel}
            inputClassName={inv.input}
            value={packs}
            onChange={setPacks}
            rules={PACKS}
            autoFocus
            testId="receive-packs"
          />
          <NumberField
            label="Price per pack on the invoice"
            required
            className={inv.field}
            labelClassName={inv.fieldLabel}
            inputClassName={inv.input}
            value={price}
            onChange={setPrice}
            rules={priceRules}
            testId="receive-price"
          />
        </div>

        <p className={styles.note}>
          Adds {arrival.words} to the shelf
          {quantityOnHand !== null &&
            packsValid &&
            `: ${String(quantityOnHand)} → ${String(quantityOnHand + arrival.added)}`}
          .
        </p>
        {short && (
          <fieldset className={styles.check} data-testid="receive-rest">
            <legend className={`${styles.note} ${styles.warm}`}>
              Fewer than the {outstanding} still to come. Is the rest coming?
            </legend>
            <label>
              <input
                type="radio"
                name="rest"
                checked={stillComing}
                onChange={() => setStillComing(true)}
                data-testid="receive-still-coming"
              />{" "}
              Still coming — keep the order open for {outstanding - Number(packs)}
            </label>
            <label>
              <input
                type="radio"
                name="rest"
                checked={!stillComing}
                onChange={() => setStillComing(false)}
                data-testid="receive-nothing-more"
              />{" "}
              Nothing more — close the order
            </label>
          </fieldset>
        )}
        {changed && cents !== null && canManagePrices && (
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={update}
              onChange={(event) => setUpdate(event.target.checked)}
              data-testid="receive-update-price"
            />
            Save {formatMoney(cents, currency)} as {order.supplierName}&apos;s price (was{" "}
            {formatMoney(order.priceCents, currency)})
          </label>
        )}

        <div className={inv.dialogActions}>
          <button type="button" className={inv.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={inv.primary}
            disabled={busy || !packsValid || cents === null}
            data-testid="receive-save"
          >
            Add to the shelf
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Why the supplier's price was not changed, when it was asked to be (IA-D4, IA-D10). */
const KEPT: Partial<Record<NonNullable<SupplierOrder["priceOutcome"]>, string>> = {
  NOT_ALLOWED: "The supplier's price was kept — changing it needs permission to manage prices.",
  PACK_CHANGED:
    "The supplier's price was kept — their pack is no longer the one ordered, so change it on their list.",
  NO_LINE: "The supplier's price was kept — that item is no longer on their list.",
};

/** The toast after *It arrived*: what reached the shelf, and anything about the price. */
export function arrivedMessage(
  order: SupplierOrder,
  body: ReceiveSupplierOrder,
  result: SupplierOrder,
): string {
  const added = `${order.itemName}: ${arrivalWords(order, body.packs).words} added to the shelf.`;
  const rest = result.status === "PARTIALLY_RECEIVED" ? " The rest stays on order." : "";
  const kept = result.priceOutcome === undefined ? undefined : KEPT[result.priceOutcome];
  return added + rest + (kept === undefined ? "" : ` ${kept}`);
}
